import { useCallback, useEffect, useRef, useState } from "react";

const POLL_INTERVAL_MS = 5 * 60_000; // 5 minutes

/**
 * Pull the hashed asset URLs out of an `index.html` string. Every deploy gives
 * these chunks new content-hash filenames, so the joined+sorted set is a
 * reliable build fingerprint — no build-time version injection or dedicated
 * version endpoint required. Only `/assets/` paths are matched so runtime
 * third-party scripts can't pollute the signature.
 */
function assetSignature(html: string): string {
  const matches = html.match(/(?:src|href)="[^"]*\/assets\/[^"]+"/g);
  return matches ? [...matches].sort().join("|") : "";
}

async function fetchSignature(): Promise<string | null> {
  try {
    const res = await fetch(`/index.html?_=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    return assetSignature(await res.text());
  } catch {
    // Offline or a transient failure — ignore and try again next tick.
    return null;
  }
}

/**
 * Polls the deployed `index.html` (on an interval and whenever the tab regains
 * focus) and returns `true` once its asset fingerprint differs from the build
 * the user is currently running — i.e. a newer version has been deployed.
 *
 * Pairs with `lazyImport`'s reactive reload: this lets us prompt the user to
 * refresh *before* they hit a 404'd chunk on a lazy route.
 */
export function useNewVersionAvailable(): boolean {
  const [isStale, setIsStale] = useState(false);
  const baselineRef = useRef<string | null>(null);

  const check = useCallback(async () => {
    if (isStale) return; // already flagged — stop polling
    const signature = await fetchSignature();
    if (!signature) return;
    if (baselineRef.current === null) {
      baselineRef.current = signature; // first successful fetch establishes the baseline
      return;
    }
    if (signature !== baselineRef.current) setIsStale(true);
  }, [isStale]);

  useEffect(() => {
    check(); // establish the baseline immediately
    const interval = window.setInterval(check, POLL_INTERVAL_MS);
    window.addEventListener("focus", check);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", check);
    };
  }, [check]);

  return isStale;
}
